import React, { useCallback } from 'react'
import { Alert, FileList } from '@pandiafi/components'
import { Heading } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'

import { API_ENDPOINTS } from '../../../common/apiEndpoints'

import { useAPIGetContractDocumentInfo } from 'api/apiHooks/useAPIGetContractDocumentInfo'
import { Section } from 'components'
import { OpenableFileListItem } from 'components/OpenableFileListItem/OpenableFileListItem'
import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'
import { useOpenFile } from 'hooks/useOpenFile/useOpenFile'

// Render only inside <Permissions feature="contractDocument">, the endpoint requires it
export const ContractDocumentSection: React.FC = () => {
  const { t } = useTranslation(['userInfo'])
  const { currentContractId } = useCurrentContract()
  const { data: contractDocument } =
    useAPIGetContractDocumentInfo(currentContractId)

  // The resident app backend returns the file content itself instead of a signed URL,
  // so useOpenFile is given that received path directly.
  // The request bypasses apiClient (which only handles JSON and normally adds the /api
  // prefix), so the prefix needs to be added here instead.
  // The content is fetched on every click, so a time-limited link is never reused.
  const getContentUrl = useCallback(
    async () =>
      `/api${API_ENDPOINTS.CONTRACT_DOCUMENT_CONTENT.replace(':uuid', currentContractId)}`,
    [currentContractId]
  )
  const { openFile, isOpening, hasOpenError } = useOpenFile(getContentUrl)

  if (!contractDocument?.available || !contractDocument.fileName) {
    return null
  }

  return (
    <Section divider>
      <Heading element="h2" size="xl" semibold className="mb-4">
        {t('userInfo:contractDocument.header')}
      </Heading>

      {hasOpenError && (
        <Alert variant="danger" className="w-full justify-start mb-4">
          {t('userInfo:contractDocument.errors.failedToOpen')}
        </Alert>
      )}

      <FileList>
        <OpenableFileListItem
          fileName={contractDocument.fileName}
          onOpen={openFile}
          isOpening={isOpening}
        />
      </FileList>
    </Section>
  )
}
